import React, { FC, useContext, ReactNode } from 'react'
import noop from 'lodash/noop'
import useSettings from '../../components/Settings/hooks/useSettings'
import { useFloor } from '@providers/mapProvider'
import useBookingDeskStore, { initialState } from './reducer'
import { BookDeskParamT, RecurrenceT, StoreT, TypePattern } from './types'
import { getNumberLimit } from './utils'
import { DateUtil, TIME } from '@utils/dateUtil'
import api from '@api'
import { FetchResult } from 'apollo-boost'

type BookingDeskProviderPropsT = { children: ReactNode }

export const BookingDeskProvider: FC<BookingDeskProviderPropsT> = ({ children }) => {
  const floor = useFloor()
  const settings = useSettings()
  const store = useBookingDeskStore()
  const timezone = floor.map?.building.timezone || ''
  const bookingWorkdayTimeFrom = floor.bookingWorkdayTimeFrom
  const allDaySetting = !settings.hourlyBookingEnabled
  const { date, recurrence } = store

  const setRecurredBooking: IBookingDeskContext['setRecurredBooking'] = (param, fromDate) => {
    const [[type, duration]] = Object.entries(settings?.maxFutureBookingDuration || { days: 1 })

    const limitation = settings.maxFutureBookingDuration
      ? DateUtil.tz(timezone).add(duration, type as TIME)
      : null

    const newLimit = getNumberLimit({
      fromDate: fromDate ? fromDate.tz(timezone) : DateUtil.tz(date.fromDate || '', timezone),
      recurrence: { ...recurrence, ...param },
      companyLimitation: limitation,
    })

    const newRangeInterval = param.rangeInterval
      ? param.rangeInterval > newLimit
        ? newLimit
        : param.rangeInterval
      : recurrence.rangeInterval > newLimit
      ? newLimit
      : recurrence.rangeInterval

    if (param.typePattern === TypePattern.WEEKLY && recurrence.typePattern !== TypePattern.WEEKLY) {
      const day = recurrence.weekdays[DateUtil.tz(timezone).day()]
      if (!recurrence.daysOfWeek.includes(day)) {
        param.daysOfWeek = [day]
      }
    }

    store.updateRecurredBooking({
      ...param,
      rangeInterval: newRangeInterval,
      numberLimit: newLimit,
    })
  }

  const bookDesk: IBookingDeskContext['bookDesk'] = (param) => {
    return api.booking.create.createBooking({
      ...param,
    })
  }

  const bookRecurringDesk: IBookingDeskContext['bookRecurringDesk'] = (param) => {
    return api.booking.create.createBookingSchedule({
      ...param,
      recurrence,
      bookingWorkdayTimeFrom,
      allDaySetting,
    })
  }

  const bookMeetingRoom: IBookingDeskContext['bookMeetingRoom'] = (param) => {
    return api.booking.create.createBooking({
      ...param,
    })
  }

  const bookMultiDayDesk: IBookingDeskContext['bookMultiDayDesk'] = (param) => {
    return api.booking.create.createSpaceBooking({
      ...param,
      recurrence,
      bookingWorkdayTimeFrom,
      allDaySetting,
      timezone,
    })
  }

  return (
    <BookingDeskContext.Provider
      value={{
        ...store,
        setRecurredBooking,
        bookDesk,
        bookRecurringDesk,
        bookMeetingRoom,
        bookMultiDayDesk,
      }}>
      {children}
    </BookingDeskContext.Provider>
  )
}

type IBookingDeskContext = StoreT & {
  setRecurredBooking: (param: Partial<RecurrenceT>, fromDate?: DateUtil) => void
  bookDesk: (param: BookDeskParamT) => Promise<FetchResult<any>>
  bookRecurringDesk: (param: BookDeskParamT) => Promise<FetchResult<any>>
  bookMeetingRoom: (param: BookDeskParamT) => Promise<FetchResult<any>>
  bookMultiDayDesk: (param: BookDeskParamT) => Promise<FetchResult<any>>
}

const initContext: IBookingDeskContext = {
  ...initialState,
  updateRecurredBooking: noop,
  updateBookingDate: noop,
  clearBookingDate: noop,
  setRecurredBooking: noop,
  clearRecurredBooking: noop,
  bookDesk: () => Promise.resolve(null as unknown as FetchResult<any>),
  bookRecurringDesk: () => Promise.resolve(null as unknown as FetchResult<any>),
  bookMeetingRoom: () => Promise.resolve(null as unknown as FetchResult<any>),
  bookMultiDayDesk: () => Promise.resolve(null as unknown as FetchResult<any>),
}

const BookingDeskContext = React.createContext<IBookingDeskContext>(initContext)

export const useBookingDesk = () => {
  return useContext(BookingDeskContext)
}
